import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { tasksService, type TaskInput } from '../../../services/projectManagement/tasksService';
import { supabase } from '../../../config/supabaseClient';
import { ENV } from '../../../config/env';

interface AIProjectAssistantProps {
  projectId: string;
  projectName: string;
  projectDescription?: string;
  organizationId: string;
  onTasksGenerated: () => void;
  onClose: () => void;
}

interface GeneratedTask {
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  estimated_time: number;
}

const AIProjectAssistant: React.FC<AIProjectAssistantProps> = ({
  projectId,
  projectName,
  projectDescription,
  organizationId,
  onTasksGenerated,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [generatedTasks, setGeneratedTasks] = useState<GeneratedTask[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<Set<number>>(new Set());
  const [additionalContext, setAdditionalContext] = useState('');

  const generateTasks = async () => {
    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {
        toast.error('Not authenticated');
        return;
      }

      if (!organizationId || !projectId) {
        toast.error('Missing project context');
        return;
      }

      const response = await fetch(`${ENV.API_URL}/api/project-management/ai/generate-tasks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          organization_id: organizationId,
          project_id: projectId,
          additional_context: additionalContext || undefined,
        }),
      });

      const result = await response.json();
      if (!response.ok) {
        toast.error(result?.error || 'Failed to generate tasks');
        return;
      }

      const tasks = (result?.data || []) as GeneratedTask[];
      setGeneratedTasks(tasks);
      setSelectedTasks(new Set(tasks.map((_, index) => index)));
      toast.success(`Generated ${tasks.length} tasks`);
    } catch (error: any) {
      console.error('Error generating tasks:', error);
      toast.error('Failed to generate tasks');
    } finally {
      setLoading(false);
    }
  };

  const toggleTaskSelection = (index: number) => {
    const newSelected = new Set(selectedTasks);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedTasks(newSelected);
  };

  const createSelectedTasks = async () => {
    if (selectedTasks.size === 0) {
      toast.error('Please select at least one task');
      return;
    }

    setLoading(true);
    try {
      const tasksToCreate = Array.from(selectedTasks).map(index => {
        const task = generatedTasks[index];
        return {
          project_id: projectId,
          organization_id: organizationId,
          title: task.title,
          description: task.description,
          status: 'backlog' as const,
          priority: task.priority,
          estimated_time: task.estimated_time,
        };
      });

      let successCount = 0;
      for (const taskData of tasksToCreate) {
        const { error } = await tasksService.create(taskData);
        if (!error) {
          successCount++;
        }
      }

      if (successCount === tasksToCreate.length) {
        toast.success(`Created ${successCount} tasks`);
        onTasksGenerated();
        onClose();
      } else {
        toast.error(`Only created ${successCount} of ${tasksToCreate.length} tasks`);
      }
    } catch (error) {
      toast.error('Failed to create tasks');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-auto rounded-xl bg-white">
        {/* Header */}
        <div className="sticky top-0 border-b border-gray-200 bg-white px-6 py-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">🤖 AI Project Assistant</h2>
              <p className="mt-1 text-sm text-gray-600">
                Generate task breakdown for <span className="font-semibold">{projectName}</span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-2 hover:bg-gray-100"
              disabled={loading}
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {generatedTasks.length === 0 ? (
            /* Generation Form */
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Additional Context (Optional)
                </label>
                <textarea
                  value={additionalContext}
                  onChange={(e) => setAdditionalContext(e.target.value)}
                  placeholder="Add any specific requirements, constraints, or goals..."
                  rows={4}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>

              <button
                onClick={generateTasks}
                disabled={loading}
                className="w-full rounded-lg bg-primary px-4 py-3 font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
              >
                {loading ? '🤖 Generating Tasks...' : '✨ Generate Task Breakdown'}
              </button>
            </>
          ) : (
            /* Generated Tasks */
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Select tasks to create ({selectedTasks.size} of {generatedTasks.length} selected)
                </p>
                <button
                  onClick={() => {
                    setGeneratedTasks([]);
                    setSelectedTasks(new Set());
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  ↻ Regenerate
                </button>
              </div>

              <div className="space-y-3">
                {generatedTasks.map((task, index) => (
                  <div
                    key={index}
                    onClick={() => toggleTaskSelection(index)}
                    className={`cursor-pointer rounded-lg border-2 p-4 transition-all ${
                      selectedTasks.has(index)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedTasks.has(index)}
                        onChange={() => toggleTaskSelection(index)}
                        className="mt-1 h-4 w-4 rounded border-gray-300"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-gray-900">{task.title}</h4>
                          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                            task.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                            task.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                            task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {task.priority}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-gray-600">{task.description}</p>
                        <p className="mt-2 text-xs text-gray-500">
                          ⏱️ Estimated: {task.estimated_time}h
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-3 sticky bottom-0 bg-white pt-4 border-t">
                <button
                  onClick={onClose}
                  className="flex-1 rounded-lg border border-gray-300 px-4 py-2 font-semibold hover:bg-gray-50"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={createSelectedTasks}
                  disabled={loading || selectedTasks.size === 0}
                  className="flex-1 rounded-lg bg-primary px-4 py-2 font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
                >
                  {loading ? 'Creating...' : `Create ${selectedTasks.size} Tasks`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIProjectAssistant;
